/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { applyResellerTheme } from "utils/reseller-theme";

interface AppConfigContextType {
  appName: string;
  // A reseller may bring their own reCAPTCHA key — that is what sidesteps Google's cap of 250
  // domains per key on the shared platform key. /branding returns it per host, so it arrives with
  // the rest of the branding and is undefined until then; consumers fall back to the build-time key.
  recaptchaSiteKey?: string;
}

interface AppConfigProviderProps {
  children: ReactNode;
}

const AppConfigContext = createContext<AppConfigContextType | undefined>(undefined);

export const useAppConfig = (): AppConfigContextType => {
  const context = useContext(AppConfigContext);
  if (!context) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  return context;
};

/**
 * Which host to ask /branding about. Normally the one in the address bar.
 *
 * DEVELOPMENT ONLY, and deliberately so: a reseller's theme applies by domain, and localhost is a
 * platform host — so running the app locally always renders the platform's own theme and there is
 * no way to see a reseller's. `?brandingHost=their-domain.com` (or REACT_APP_BRANDING_HOST) borrows
 * that domain's branding for the local tab.
 *
 * It is gated on NODE_ENV because in production the same parameter would let anyone render
 * app.qcall.ai in someone else's brand — a convincing phishing page built out of our own URL. The
 * build strips this branch, so the parameter does nothing on a deployed site.
 */
const brandingHost = (): string => {
  if (process.env.NODE_ENV === "development") {
    const override =
      new URLSearchParams(window.location.search).get("brandingHost") ||
      process.env.REACT_APP_BRANDING_HOST;
    if (override) return override;
  }
  return window.location.hostname;
};

const applySettingsToDOM = (
  settings: any,
  setAppName: (n: string) => void,
  setRecaptchaSiteKey: (k?: string) => void
) => {
  setRecaptchaSiteKey(settings.recaptcha_site_key || undefined);

  const audioUrl = process.env.REACT_APP_URL_FOR_AUDIO || "";

  // Meta description
  const metaDescription = document.querySelector("meta[name='description']");
  if (metaDescription) {
    metaDescription.setAttribute("content", settings.website_description || "");
  } else {
    const newMeta = document.createElement("meta");
    newMeta.name = "description";
    newMeta.content = settings.website_description || "";
    document.head.appendChild(newMeta);
  }

  // Page title / app name
  if (settings.website_name) setAppName(settings.website_name);

  // Favicon
  const faviconHref = settings.favicon_logo
    ? audioUrl + settings.favicon_logo
    : "";
  if (faviconHref) {
    const linkFavicon = document.querySelector("link[rel='icon']");
    if (linkFavicon) {
      (linkFavicon as HTMLLinkElement).href = faviconHref;
    } else {
      const newFavicon = document.createElement("link");
      newFavicon.rel = "icon";
      newFavicon.href = faviconHref;
      document.head.appendChild(newFavicon);
    }
  }

  // The reseller theme. Guarded inside applyResellerTheme by `is_white_label`, which is what keeps
  // the platform's own colours untouched: on qcall.ai this CLEARS every theme variable rather than
  // setting one, so a cached appSetting from a reseller domain cannot leak a colour across.
  //
  // This used to set --brand-primary / --brand-secondary unconditionally — and nothing read either
  // variable, so a reseller could save a colour and watch nothing happen.
  applyResellerTheme(settings);
};

export const AppConfigProvider: React.FC<AppConfigProviderProps> = ({ children }) => {
  const [appName, setAppName] = useState<string>("");
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState<string | undefined>(undefined);

  useEffect(() => {
    const fetchConfig = async () => {
      const baseUrl = process.env.REACT_APP_BASE_URL;

      // Step 1: Try per-domain /branding endpoint first
      try {
        const brandRes = await fetch(`${baseUrl}/branding?host=${brandingHost()}`);
        const brandData = await brandRes.json();
        if (brandData?.success && brandData?.data?.length > 0) {
          const settings = brandData.data[0];
          // Normalize white-label branding keys to match existing appSetting shape.
          // The /branding response keys intentionally match /getSettings keys already.
          const merged = {
            ...JSON.parse(localStorage.getItem("appSetting") || "{}"),
            ...settings,
          };
          localStorage.setItem("appSetting", JSON.stringify(merged));
          applySettingsToDOM(settings, setAppName, setRecaptchaSiteKey);
          return; // branding loaded — skip fallback
        }
      } catch {
        // branding endpoint unavailable — fall through to /getSettings
      }

      // Step 2: Fallback — existing /getSettings call (platform domain unchanged)
      try {
        const response = await fetch(`${baseUrl}/getSettings`);
        const data = await response.json();
        if (data.success && data?.data?.length > 0) {
          localStorage.setItem("appSetting", JSON.stringify(data.data[0]));
          applySettingsToDOM(data.data[0], setAppName, setRecaptchaSiteKey);
        } else {
          localStorage.setItem("appSetting", JSON.stringify({}));
        }
      } catch {
        localStorage.setItem("appSetting", JSON.stringify({}));
      }
    };

    fetchConfig();
  }, []);

  return (
    <AppConfigContext.Provider value={{ appName, recaptchaSiteKey }}>
      {children}
    </AppConfigContext.Provider>
  );
};
